import type {
  InitTestChatPayload,
  InitTestChatResponse,
  StreamRequestBody,
  ServiceResponse,
  UUID,
  ApiErrorResponse,
  SSEMessageEventData,
  ToolResultPayload,
} from '@dopomogai/types';
import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';

interface FetchSSEError {
    type: 'error' | 'abort';
    message: string;
    status?: number;
    originalError?: Error | any;
    isConfigurationError?: boolean;
}

// Exported to be used by the useConversation hook
export interface FetchSSEOpenEvent {
    type: 'open';
    message: string;
    headers?: Record<string, string>; // Crucial for passing WAT refresh header
}

/**
 * NOTE: While named 'agentTestChatService', its `streamTestConversation` and
 * `submitToolResultAndStream` methods are used by BOTH test chat and public widget
 * flows, as they share the same SSE streaming endpoint. The backend differentiates
 * them based on the Widget Access Token's (WAT) 'tut' claim.
 */
export const agentTestChatService = {
  /**
   * Initializes a secure test chat session for an authenticated user.
   * This should ONLY be called in an environment where a user is logged in.
   */
  initSession: (
    agentId: UUID,
    initialContext?: InitTestChatPayload['initial_context']
  ): ServiceResponse<InitTestChatResponse> => {
    if (!agentId) {
        const error = { isError: true, message: 'Agent ID is required.', status: 400, originalError: new Error("Agent ID required") } as ApiErrorResponse;
        return Promise.resolve(error);
    }
    const payload: InitTestChatPayload = initialContext ? { initial_context: initialContext } : {};
    return getApiClient().post<InitTestChatResponse>(`/agents/${agentId}/init-test-chat`, payload)
      .then(response => response.data)
      .catch(error => handleApiError(error, `initialize test chat session for agent ${agentId}`));
  },

  /**
   * Creates and manages a Server-Sent Events (SSE) stream for a conversation.
   * This is a generic function used by both test and widget auth modes.
   */
  streamTestConversation: (
    rootApiUrl: string,
    agentId: UUID,
    requestBody: StreamRequestBody,
    wat: string, // Widget Access Token (from either init flow)
    onMessage: (data: SSEMessageEventData) => void,
    onError: (errorDetails: FetchSSEError) => void,
    onEnd?: () => void,
    onOpen?: (eventDetails: FetchSSEOpenEvent) => void
  ): { close: () => void } => {
    const controller = new AbortController();
    let closedManually = false;

    const errorCallback = (details: FetchSSEError) => {
      if (closedManually && details.type === 'abort') return;
      onError(details);
    };

    if (!rootApiUrl || !agentId || !requestBody || !wat) {
      errorCallback({ type: 'error', message: "streamTestConversation: Missing required parameters", isConfigurationError: true });
      return { close: () => {} };
    }

    const cleanRootApiUrl = rootApiUrl.endsWith('/') ? rootApiUrl.slice(0, -1) : rootApiUrl;
    const sseUrl = `${cleanRootApiUrl}/api/v1/agents/${agentId}/conversations/stream`;

    const connect = async () => {
      try {
        const response = await fetch(sseUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${wat}`, 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });

        if (!response.ok) {
          let errorData;
          try { errorData = await response.json(); } catch (e) {
            errorData = { detail: await response.text() || `Request failed with status ${response.status}` };
          }
          const errorMessage = errorData.detail || `An unknown error occurred (${response.status})`;
          throw new Error(errorMessage);
        }

        // Pass headers to the onOpen callback for WAT refresh handling
        if (onOpen) onOpen({ type: 'open', message: 'Connection established', headers: Object.fromEntries(response.headers.entries()) });

        if (!response.body) throw new Error("Response body is null, cannot read stream.");

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          let boundaryIndex;
          while ((boundaryIndex = buffer.indexOf('\n\n')) >= 0) {
            const messageBlock = buffer.substring(0, boundaryIndex);
            buffer = buffer.substring(boundaryIndex + 2);

            if (messageBlock.startsWith('data:')) {
              const jsonString = messageBlock.substring(5).trim();
              if (jsonString) {
                try {
                  const parsedData = JSON.parse(jsonString);
                  onMessage(parsedData);
                } catch (e) {
                  console.error('SSE JSON Parse Error:', e, 'on string:', jsonString);
                  onError({ type: 'error', message: 'Failed to parse an SSE message.', originalError: e });
                }
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          errorCallback({ type: 'abort', message: 'Stream aborted by client.' });
        } else {
          errorCallback({ type: 'error', message: error.message, status: error.status, originalError: error });
        }
      } finally {
        if (onEnd) onEnd();
      }
    };

    connect();

    return {
      close: () => {
        if (!closedManually) {
          closedManually = true;
          controller.abort();
        }
      }
    };
  },
  
  /**
   * Submits the result of an interactive tool and streams the agent's subsequent response.
   * This is a generic function used by both test and widget auth modes.
   */
  submitToolResultAndStream: (
    rootApiUrl: string,
    agentId: UUID,
    conversationId: UUID,
    toolCallId: string,
    resultPayload: ToolResultPayload,
    wat: string,
    onMessage: (data: SSEMessageEventData) => void,
    onError: (errorDetails: FetchSSEError) => void,
    onEnd?: () => void,
    onOpen?: (eventDetails: FetchSSEOpenEvent) => void
  ): { close: () => void } => {
      // For this function to work correctly, it needs the same full implementation
      // as `streamTestConversation`, just with a different endpoint URL.
      const controller = new AbortController();
      let closedManually = false;

      const errorCallback = (details: FetchSSEError) => {
          if (closedManually && details.type === 'abort') return;
          onError(details);
      };

      if (!rootApiUrl || !agentId || !conversationId || !toolCallId || !resultPayload || !wat) {
          errorCallback({ type: 'error', message: "submitToolResultAndStream: Missing required parameters", isConfigurationError: true });
          return { close: () => {} };
      }

      const cleanRootApiUrl = rootApiUrl.endsWith('/') ? rootApiUrl.slice(0, -1) : rootApiUrl;
      const sseUrl = `${cleanRootApiUrl}/api/v1/agents/${agentId}/conversations/${conversationId}/tools/${toolCallId}/result/stream`;

      const connect = async () => {
          try {
              const response = await fetch(sseUrl, {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${wat}`, 'Content-Type': 'application/json', 'Accept': 'text/event-stream' },
                  body: JSON.stringify(resultPayload),
                  signal: controller.signal,
              });

              if (!response.ok) {
                  let errorData;
                  try { errorData = await response.json(); } catch (e) {
                      errorData = { detail: await response.text() || `Request failed with status ${response.status}` };
                  }
                  throw new Error(errorData.detail || `An unknown error occurred (${response.status})`);
              }

              if (onOpen) onOpen({ type: 'open', message: 'Synthesis stream established', headers: Object.fromEntries(response.headers.entries()) });
              
              if (!response.body) throw new Error("Response body is null, cannot read stream.");

              const reader = response.body.getReader();
              const decoder = new TextDecoder("utf-8");
              let buffer = '';

              while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;

                  buffer += decoder.decode(value, { stream: true });
                  let boundaryIndex;
                  while ((boundaryIndex = buffer.indexOf('\n\n')) >= 0) {
                      const messageBlock = buffer.substring(0, boundaryIndex);
                      buffer = buffer.substring(boundaryIndex + 2);
                      if (messageBlock.startsWith('data:')) {
                          const jsonString = messageBlock.substring(5).trim();
                          if (jsonString) {
                              try { onMessage(JSON.parse(jsonString)); } catch (e) {
                                  onError({ type: 'error', message: 'Failed to parse an SSE message.', originalError: e });
                              }
                          }
                      }
                  }
              }
          } catch (error: any) {
              if (error.name === 'AbortError') {
                  errorCallback({ type: 'abort', message: 'Synthesis stream aborted by client.' });
              } else {
                  errorCallback({ type: 'error', message: error.message, status: error.status, originalError: error });
              }
          } finally {
              if (onEnd) onEnd();
          }
      };

      connect();

      return {
          close: () => {
              if (!closedManually) {
                  closedManually = true;
                  controller.abort();
              }
          }
      };
  }
};